import React, { useState, useEffect, useMemo } from "react";
import { Filters } from "@rankmi/rankmi-table";
import { filtersLocale } from "@frontend/src/utilities/filter_config";
import { ListService } from "@services/list_service";
import { openNotificationWithIcon } from "@frontend/src/utilities/notificationWithIcon";

const reference = {
  eq: 1,
  not_eq: 3,
  gt: 2,
  lt: 0,
  cont: 4,
  not_cont: 5,
  null: 6,
  not_null: 7,
  start: 8,
  end: 9,
  true: 10,
  false: 11
};

const stringFilterOptions = [
  {
    label: "Contiene",
    value: "cont",
  },
  {
    label: "No contiene",
    value: "not_cont",
  },
  {
    label: "Es igual a",
    value: "eq",
  },
  {
    label: "No es",
    value: "not_eq",
  },
  {
    label: "Comienza con",
    value: "starts_with",
  },
  {
    label: "Termina con",
    value: "ends_with",
  },
  {
    label: "Está vacío",
    value: "null",
  },
  {
    label: "No está vacío",
    value: "not_null",
  },
]

const numberFilterOptions = [
  {
    label: "Es igual a",
    value: "eq",
  },
  {
    label: "No es",
    value: "not_eq",
  },
  {
    label: "Es mayor que",
    value: "gt",
  },
  {
    label: "Es menor que",
    value: "lt",
  },
];

const booleanOptions = [
  {
    label: "Es verdadero",
    value: "true",
  },
  {
    label: "Es falso",
    value: "false",
  }
];

const presenceOptions = [
  {
    label: "Está vacío",
    value: "null",
  },
  {
    label: "No está vacío",
    value: "not_null",
  },
]

const filtersOptionsTest = [
  {
    label: "Mettl",
    value: "mettl"
  }
]

const filterOptionsExternal = [
  {
    label: "Es",
    value: "mettl"
  }
]
const excludeOperators = ["null", "not_null", "cont", "not_cont", "gteq", "lteq" ]

const NUMBER_OPERATORS = ["eq", "not_eq", "gt", "lt"]
const BOOLEAN_OPERATORS = ["true", "false"]

const operatorFromCompare = (compare) =>
  Object.keys(reference).find((key) => reference[key] === compare)

// rankmi-table reads operatorData.value without a guard; a stored
// text operator on a number/boolean column throws when opening the filter.
const hydrateOperator = (operator, fieldType) => {
  if (!operator) return operator
  if (fieldType === "number") {
    if (NUMBER_OPERATORS.includes(operator)) return operator
    if (operator === "not_cont") return "not_eq"
    return "eq"
  }
  if (fieldType === "boolean") {
    if (BOOLEAN_OPERATORS.includes(operator)) return operator
    if (operator === "not_cont" || operator === "not_eq") return "false"
    return "true"
  }
  return operator
}

const FiltersViewerModal = ({ value, rules, openModal, onChangeModal, hybridOptions, hybridSearch }) => {

  const [data, setData] = useState({});
  const [chargingData, setChargingData] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const service = new ListService();

  useEffect(() => {
    if ( value ) fetchingRules();
  }, [value]);

  useEffect(() => {
    setSelectedFilters(getSelectedFilters());
  }, [data])

  function isStringNumber(str) {
    return !Number.isNaN(Number(str));
  }

  const getCatalogType = (element) => {
    const entry = Object.values(rules).find((rule) => rule.value === element)
    if (!entry) return null
    return translateTypes(entry.type)
  }

  const resolveFilterType = (elem, catalogType) => {
    if (catalogType === "boolean" || catalogType === "number") return catalogType
    if (elem.category === "value") {
      return isStringNumber(elem.value) ? "number" : "string"
    }
    return "list"
  }

  const getSelectedFilters = () => {
    if(Object.keys(data).length > 0) {
      const result = Object.entries(data).length !== 0
        ? {
            generalCondition: data.global_comparer,
            filterType: "simple",
            data: data.rules.map((elem) => {
              if(elem.category === 'test') {
                const testValue = elem.element.split('&&')
                const childType = Number(elem.value) ? "number" : "list"
                return {
                  id: elem.id,
                  input: 'tests',
                  type: 'group',
                  operator: 'mettl',
                  compareTo: Number(testValue[1]),
                  childrensGroup: {
                    input: testValue[0],
                    type: childType,
                    operator: hydrateOperator(
                      operatorFromCompare(elem.compare),
                      childType
                    ),
                    compareTo: elem.value,
                  }
                }
              }
              const catalogType = getCatalogType(elem.element)
              const type = resolveFilterType(elem, catalogType)
              return {
                id: elem.id,
                input: elem.element,
                type,
                operator: hydrateOperator(
                  operatorFromCompare(elem.compare),
                  catalogType || type
                ),
                compareTo: elem.value,
            }}),
          }
        : null;
        return result;
      }
      return null;
  }

  const fetchingRules = () => {
    setChargingData(true);
    service.fetchRules(
      value,
      (data) => {
        const found = data.rules.find(elem => elem.element === 'university')
        if(found) {
          hybridSearch.universities(found.value)
        }
        setData(data);
        setChargingData(false);
      },
      console.error
    );
  };

  const translateTypes = (value) => {
    if (value === "text") {
      return "string"
    }
    if (value === "number") {
      return "number"
    }
    return value
  }

  const columns = useMemo(() => {
    const aux = []
    Object.keys(rules).forEach((value) => {
      const type = translateTypes(rules[value]["type"])
      if (rules[value]["type"] === "list") {
        aux.push({
          label: value,
          value: rules[value].value,
          type: "list",
          excludedOperators: excludeOperators,
          list: rules[value].options.map((elem) => ({
            label: elem.label,
            value: elem.value,
          })),
        });
      } else if (rules[value]["type"] === "group")  {
        aux.push({
          label: value,
          value: rules[value].value,
          type: "group",
          filterOptions: rules[value]["externalTests"] ? filterOptionsExternal : filtersOptionsTest,
          list: rules[value].options.map((elem) => ({
            label: elem.label,
            value: elem.value,
            childrensGroup: elem.childrensGroup.map(dimention => {
              let value = {
                label: dimention.label,
                value: dimention.label,
                type: dimention.type,
                excludedOperators: excludeOperators,
                min: 0
              }
              if (dimention.type === 'list') {
                value = {
                  ...value,
                  list: dimention.list.map((item) => ({
                    label: item.label,
                    value: item.label,
                    type: item.type
                  }))
                }
              }
              return value
            })
          }))
        });
      }
      else if (rules[value]["type"] === "hybrid") {
        aux.push({
          label: "Universidad",
          value: "university",
          type: "hybrid",
          list: hybridOptions.universities,
          searchStringKey: "university.label",
          onSearch: (text) => { hybridSearch.universities(text) }
        })
      }
      else {
        aux.push({
          label: value,
          value: rules[value].value,
          type: type,
          filterOptions: type === 'boolean' ? booleanOptions
            : type === 'presence' ? presenceOptions
            : (rules[value]["type"] === "number") ? numberFilterOptions
              : stringFilterOptions,
        })
      }
    });

  return aux
  }, [rules, hybridOptions]);

  const createRules = (rules) => {
    const request = {
      list: {
        global_comparer: rules["generalCondition"],
        rules_attributes: rules["data"].map((elem) => {

        if(elem.input === "tests") {
          return {
            id: elem.id,
            value: elem.childrensGroup.type !== 'number'
            ? elem.childrensGroup.compareTo
            : Number(elem.childrensGroup.compareTo),
            compare: reference[elem.childrensGroup.operator],
            element: elem.childrensGroup.input + `&&${elem.compareTo}`,
            category: "test"
          }
        }
        else if (elem.input === "university") {
          return {
            id: elem.id,
            value: elem.compareTo.toString(),
            compare: reference[elem.operator],
            element: "university",
            category: "university"
          }
        }
        else {
          debugger
          return {
            id: elem.id,
            value: elem["compareTo"],
            compare: reference[elem["operator"]],
            element: elem["input"],
            category: ["string", "number", "boolean"].includes(elem["type"]) ? "value" : "option"
          }}
        })
      },
    };
    service.createRule(
      value,
      request,
      (data) => {
        setData(data);
        openNotificationWithIcon(
          "success",
          "Reglas actualizadas correctamente",
          ""
        );
      },
      console.error
    );
  };

  return (
    <>
      <Filters
        customTrigger
        open={openModal}
        onVisibilityChange={(state) => {
          onChangeModal(state);
        }}
        columns={columns}
        onlySimpleFilters
        locale={filtersLocale}
        selectedFilters={selectedFilters}
        onChange={(res) => {
          createRules(res)
        }}
        loading={chargingData}
      />
    </>
  );
};

export default FiltersViewerModal;
